import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { tenantApi } from '../lib/tenant-api';

const QUERY_KEY = ['embeddingSearchGuardrails'] as const;

export default function EmbeddingSearchGuardrailsSection() {
  const queryClient = useQueryClient();
  const [draftToken, setDraftToken] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: tenantApi.getEmbeddingSearchGuardrails,
  });

  const saveMutation = useMutation({
    mutationFn: tenantApi.updateEmbeddingSearchGuardrails,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: unknown) => {
      let msg = 'Failed to save settings.';
      if (err instanceof Error) msg = err.message;
      else if (typeof err === 'string') msg = err;
      toast.error(msg);
    },
  });

  const caseInsensitive = data?.case_insensitivity ?? false;
  const exemptions = data?.exemption_list ?? [];

  const handleToggle = async (next: boolean) => {
    await saveMutation.mutateAsync({ case_insensitivity: next });
  };

  const handleAddToken = async () => {
    const t = draftToken.trim();
    if (!t) return;
    if (exemptions.includes(t)) {
      setDraftToken('');
      return;
    }
    await saveMutation.mutateAsync({
      exemption_list: [...exemptions, t],
    });
    setDraftToken('');
  };

  const handleRemoveToken = async (token: string) => {
    await saveMutation.mutateAsync({
      exemption_list: exemptions.filter((x) => x !== token),
    });
  };

  const busy = isLoading || saveMutation.isPending;

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-lg font-bold leading-7 text-gray-900 sm:truncate sm:tracking-tight">
            Case In-sensitive Tokens
          </h1>
          <p className="mt-1 text-sm text-gray-700">
            When enabled, embedding similarity search uses case-insensitive matching unless the query contains one of
            the exemption strings (matched with original casing).
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-8">
        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div>
            <p className="text-sm font-medium text-gray-900">Case-insensitive search</p>
            <p className="text-xs text-gray-500">Off by default; applies to Embeddings similarity search.</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={caseInsensitive}
            disabled={busy}
            onClick={() => void handleToggle(!caseInsensitive)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              caseInsensitive ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                caseInsensitive ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        <div>
          <h2 className="text-sm font-medium text-gray-900">Exemption list</h2>
          <p className="mt-1 text-xs text-gray-500">
            Add tokens or phrases that must keep case-sensitive matching inside the query (for example brand names).
          </p>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={draftToken}
              onChange={(e) => setDraftToken(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddToken();
                }
              }}
              disabled={busy || !caseInsensitive}
              placeholder={caseInsensitive ? 'e.g. LawmaBase' : 'Enable case-insensitive search to add exemptions'}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed sm:max-w-md"
            />
            <button
              type="button"
              onClick={() => void handleAddToken()}
              disabled={busy || !caseInsensitive || !draftToken.trim()}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </button>
          </div>

          {exemptions.length > 0 ? (
            <ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
              {exemptions.map((token) => (
                <li
                  key={token}
                  className="flex items-center justify-between px-4 py-3 text-sm text-gray-800"
                >
                  <span className="font-mono">{token}</span>
                  <button
                    type="button"
                    onClick={() => void handleRemoveToken(token)}
                    disabled={busy}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-gray-500">No exemption tokens yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
